/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * FX Client Service - Frontend Currency Conversion
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Part of FinList FX Engine Phase 3
 * 
 * This client communicates with the backend FX API to get exchange rates
 * and convert amounts between currencies. All actual FX API calls happen
 * on the backend to keep the API key secure.
 * 
 * Features:
 *   - Get exchange rate between two currencies
 *   - Convert amounts with automatic rate fetching
 *   - Client-side caching to reduce API calls
 *   - Graceful error handling
 */

import api from './api';

// ════════════════════════════════════════════════════════════════════════════════
// Client-side cache for rates (reduces redundant API calls)
// ════════════════════════════════════════════════════════════════════════════════

const FX_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes (client-side)
const rateCache = new Map();

/**
 * Get cache key for a currency pair
 */
function getCacheKey(from, to) {
  return `${from.toUpperCase()}_${to.toUpperCase()}`;
}

/**
 * Check if cached rate is still valid
 */
function isCacheValid(cacheEntry) {
  if (!cacheEntry) return false;
  return Date.now() - cacheEntry.timestamp < FX_CACHE_TTL_MS;
}

// ════════════════════════════════════════════════════════════════════════════════
// Core FX Functions
// ════════════════════════════════════════════════════════════════════════════════

/**
 * Get exchange rate between two currencies
 * 
 * @param {string} from - Source currency code (e.g., 'USD')
 * @param {string} to - Target currency code (e.g., 'NGN')
 * @returns {Promise<{rate: number, from: string, to: string, cached: boolean}>}
 * 
 * @example
 * const result = await getFxRate('USD', 'NGN');
 * console.log(result.rate); // 1650.45
 */
export async function getFxRate(from, to) {
  const fromCode = from?.toUpperCase();
  const toCode = to?.toUpperCase();

  // Validate inputs
  if (!fromCode || !toCode) {
    throw new Error('Both "from" and "to" currency codes are required');
  }

  // Same currency = rate of 1
  if (fromCode === toCode) {
    return {
      rate: 1,
      from: fromCode,
      to: toCode,
      lastUpdated: new Date().toISOString(),
      cached: true
    };
  }

  // Check client-side cache first
  const cacheKey = getCacheKey(fromCode, toCode);
  const cached = rateCache.get(cacheKey);
  
  if (isCacheValid(cached)) {
    return {
      rate: cached.rate,
      from: fromCode,
      to: toCode,
      lastUpdated: cached.lastUpdated,
      cached: true
    };
  }

  try {
    // Fetch from backend
    const response = await api.get('/fx/rate', {
      params: { from: fromCode, to: toCode }
    });

    // The api service may return data directly or wrapped in response.data
    const data = response.data || response;
    const rate = data.rate;
    const lastUpdated = data.lastUpdated;  // When the FX API last updated rates
    
    if (rate === undefined || rate === null) {
      throw new Error('Invalid rate received from API');
    }

    // Cache the rate with lastUpdated timestamp
    rateCache.set(cacheKey, {
      rate,
      lastUpdated,
      timestamp: Date.now()
    });

    // Also cache the inverse rate
    const inverseKey = getCacheKey(toCode, fromCode);
    rateCache.set(inverseKey, {
      rate: 1 / rate,
      lastUpdated,
      timestamp: Date.now()
    });

    return {
      rate,
      from: fromCode,
      to: toCode,
      lastUpdated,  // Include when the rate was last updated by the API
      cached: false
    };

  } catch (error) {
    console.error('FX rate fetch error:', error);
    
    // If we have a stale cache entry, use it as fallback
    if (cached) {
      console.warn('Using stale FX rate due to API error');
      return {
        rate: cached.rate,
        from: fromCode,
        to: toCode,
        cached: true,
        stale: true
      };
    }

    // If FX service is not configured, return 1:1 rate silently
    // This prevents error toasts when FX is not set up
    if (error.response?.status === 500 || error.message?.includes('not configured')) {
      console.warn('FX service not configured, using 1:1 rate');
      return {
        rate: 1,
        from: fromCode,
        to: toCode,
        cached: false,
        unavailable: true
      };
    }

    throw new Error(
      error.response?.data?.message || 
      'Unable to fetch exchange rate. Please try again.'
    );
  }
}

/**
 * Convert an amount from one currency to another
 * 
 * @param {number} amount - Amount to convert
 * @param {string} from - Source currency code (e.g., 'USD')
 * @param {string} to - Target currency code (e.g., 'NGN')
 * @returns {Promise<{original: number, converted: number, rate: number, from: string, to: string}>}
 * 
 * @example
 * const result = await convertCurrency(100, 'USD', 'NGN');
 * console.log(result.converted); // 165045.00
 */
export async function convertCurrency(amount, from, to) {
  const parsedAmount = parseFloat(amount);
  
  if (isNaN(parsedAmount) || parsedAmount < 0) {
    throw new Error('Amount must be a non-negative number');
  }

  const fromCode = from?.toUpperCase();
  const toCode = to?.toUpperCase();

  // Same currency = no conversion
  if (fromCode === toCode) {
    return {
      original: parsedAmount,
      converted: parsedAmount,
      rate: 1,
      from: fromCode,
      to: toCode
    };
  }

  // Get rate (may be cached)
  const { rate } = await getFxRate(fromCode, toCode);
  
  // Convert and round to 2 decimal places
  const converted = Math.round(parsedAmount * rate * 100) / 100;

  return {
    original: parsedAmount,
    converted,
    rate,
    from: fromCode,
    to: toCode
  };
}

/**
 * Batch convert multiple amounts (uses single rate fetch)
 * 
 * @param {number[]} amounts - Array of amounts to convert
 * @param {string} from - Source currency code
 * @param {string} to - Target currency code
 * @returns {Promise<{amounts: number[], rate: number, from: string, to: string}>}
 * 
 * @example
 * const result = await batchConvert([100, 50, 25], 'USD', 'NGN');
 * console.log(result.amounts); // [165045.00, 82522.50, 41261.25]
 */
export async function batchConvert(amounts, from, to) {
  if (!Array.isArray(amounts)) {
    throw new Error('amounts must be an array');
  }

  const fromCode = from?.toUpperCase();
  const toCode = to?.toUpperCase();

  // Same currency = no conversion
  if (fromCode === toCode) {
    return {
      amounts: amounts.map(a => parseFloat(a) || 0),
      rate: 1,
      from: fromCode,
      to: toCode
    };
  }

  // Get rate once (may be cached)
  const { rate } = await getFxRate(fromCode, toCode);
  
  // Convert all amounts
  const convertedAmounts = amounts.map(amount => {
    const parsed = parseFloat(amount) || 0;
    return Math.round(parsed * rate * 100) / 100;
  });

  return {
    amounts: convertedAmounts,
    rate,
    from: fromCode,
    to: toCode
  };
}

// ════════════════════════════════════════════════════════════════════════════════
// Cache Management
// ════════════════════════════════════════════════════════════════════════════════

/**
 * Clear the client-side rate cache
 * Useful after user changes comparison currency
 */
export function clearFxCache() {
  rateCache.clear();
}

/**
 * Get cache status (for debugging)
 */
export function getFxCacheStatus() {
  const entries = {};
  rateCache.forEach((value, key) => {
    entries[key] = {
      rate: value.rate,
      age: Math.round((Date.now() - value.timestamp) / 1000) + 's',
      valid: isCacheValid(value)
    };
  });
  return {
    size: rateCache.size,
    entries
  };
}

// ════════════════════════════════════════════════════════════════════════════════
// Formatting Utilities
// ════════════════════════════════════════════════════════════════════════════════

/**
 * Format converted amount with currency symbol
 * 
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (e.g., 'USD', 'NGN')
 * @param {object} options - Intl.NumberFormat options
 * @returns {string} Formatted amount (e.g., "$1,650.45" or "₦165,045.00")
 */
export function formatCurrency(amount, currency, options = {}) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...options
    }).format(amount);
  } catch (error) {
    // Fallback for unsupported currencies
    return `${currency?.toUpperCase() || ''} ${amount.toFixed(2)}`;
  }
}

/**
 * Get currency symbol for a currency code
 * 
 * @param {string} currency - Currency code (e.g., 'USD', 'NGN')
 * @returns {string} Currency symbol (e.g., '$', '₦')
 */
export function getCurrencySymbol(currency) {
  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(0);
    
    // Extract symbol (remove digits)
    return formatted.replace(/[\d,.\s]/g, '').trim() || currency?.toUpperCase();
  } catch {
    return currency?.toUpperCase() || '$';
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// Common Currency List
// ════════════════════════════════════════════════════════════════════════════════

/**
 * List of commonly used currencies for user selection
 */
export const COMMON_CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' }
];

export default {
  getFxRate,
  convertCurrency,
  batchConvert,
  clearFxCache,
  getFxCacheStatus,
  formatCurrency,
  getCurrencySymbol,
  COMMON_CURRENCIES
};
